/**
 * API Client
 * 
 * Handles all API requests with automatic token refresh.
 * Returns the raw API response which follows the CMS format:
 * - Single item: { data: T }
 * - List: { data: T[], meta?: { total_count, filter_count } }
 */

import { useAuthStore } from '@/stores/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * Standard API response for single items
 */
export interface ApiResponse<T> {
    data: T;
}

/**
 * Standard API response for lists with optional metadata
 */
export interface ApiListResponse<T> {
    data: T[];
    meta?: {
        total_count?: number;
        filter_count?: number;
    };
}

/**
 * API error response structure
 */
export interface ApiErrorResponse {
    errors?: Array<{
        message: string;
        extensions?: {
            code: string;
        };
    }>;
}

interface RequestOptions extends RequestInit {
    skipAuth?: boolean;
}

class ApiClient {
    private baseUrl: string;
    private refreshPromise: Promise<boolean> | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * Get auth headers
     */
    private getAuthHeaders(): Record<string, string> {
        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
            return { Authorization: `Bearer ${accessToken}` };
        }
        return {};
    }

    /**
     * Refresh access token
     */
    async refreshToken(): Promise<boolean> {
        // Prevent multiple simultaneous refresh calls
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this._doRefresh();
        const result = await this.refreshPromise;
        this.refreshPromise = null;
        return result;
    }

    private async _doRefresh(): Promise<boolean> {
        const { refreshToken, setTokens, logout } = useAuthStore.getState();

        if (!refreshToken) {
            logout();
            return false;
        }

        try {
            const response = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
                logout();
                return false;
            }

            const data: ApiResponse<{
                access_token: string;
                refresh_token: string;
                expires: number;
            }> = await response.json();

            if (data.data) {
                setTokens(data.data.access_token, data.data.refresh_token);
                return true;
            }

            logout();
            return false;
        } catch {
            logout();
            return false;
        }
    }

    /**
     * Make an API request
     * Returns the raw JSON response from the API
     */
    async request<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<T> {
        const { skipAuth = false, headers = {}, body, ...restOptions } = options;

        const requestHeaders: Record<string, string> = {
            ...(headers as Record<string, string>),
        };

        // Only set Content-Type for non-FormData bodies
        if (!(body instanceof FormData)) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        if (!skipAuth) {
            Object.assign(requestHeaders, this.getAuthHeaders());
        }

        let response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...restOptions,
            body,
            headers: requestHeaders,
        });

        // If unauthorized, try to refresh token and retry
        if (response.status === 401 && !skipAuth) {
            const refreshed = await this.refreshToken();
            if (refreshed) {
                // Retry with new token
                Object.assign(requestHeaders, this.getAuthHeaders());
                response = await fetch(`${this.baseUrl}${endpoint}`, {
                    ...restOptions,
                    body,
                    headers: requestHeaders,
                });
            }
        }

        // Handle no content response
        if (response.status === 204) {
            return undefined as T;
        }

        const data = await response.json();

        if (!response.ok) {
            const errorData = data as ApiErrorResponse;
            throw new ApiError(
                errorData.errors?.[0]?.message || 'Request failed',
                errorData.errors?.[0]?.extensions?.code || 'UNKNOWN_ERROR',
                response.status
            );
        }

        return data as T;
    }

    /**
     * GET request
     */
    async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, { ...options, method: 'GET' });
    }

    /**
     * POST request
     */
    async post<T>(
        endpoint: string,
        body?: unknown,
        options?: RequestOptions
    ): Promise<T> {
        // Handle FormData separately - don't stringify it
        const requestBody = body instanceof FormData ? body : body ? JSON.stringify(body) : undefined;

        return this.request<T>(endpoint, {
            ...options,
            method: 'POST',
            body: requestBody,
        });
    }

    /**
     * PUT request
     */
    async put<T>(
        endpoint: string,
        body?: unknown,
        options?: RequestOptions
    ): Promise<T> {
        // Handle FormData separately - don't stringify it
        const requestBody = body instanceof FormData ? body : body ? JSON.stringify(body) : undefined;

        return this.request<T>(endpoint, {
            ...options,
            method: 'PUT',
            body: requestBody,
        });
    }

    /**
     * PATCH request
     */
    async patch<T>(
        endpoint: string,
        body?: unknown,
        options?: RequestOptions
    ): Promise<T> {
        // Handle FormData separately - don't stringify it
        const requestBody = body instanceof FormData ? body : body ? JSON.stringify(body) : undefined;

        return this.request<T>(endpoint, {
            ...options,
            method: 'PATCH',
            body: requestBody,
        });
    }

    /**
     * DELETE request
     */
    async delete<T>(
        endpoint: string,
        body?: unknown,
        options?: RequestOptions
    ): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'DELETE',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * Get blob (for file downloads and images)
     */
    async getBlob(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<Blob> {
        const { skipAuth = false, headers = {}, ...restOptions } = options;

        const requestHeaders: Record<string, string> = {
            ...(headers as Record<string, string>),
        };

        if (!skipAuth) {
            Object.assign(requestHeaders, this.getAuthHeaders());
        }

        let response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...restOptions,
            method: 'GET',
            headers: requestHeaders,
        });

        // If unauthorized, try to refresh token and retry
        if (response.status === 401 && !skipAuth) {
            const refreshed = await this.refreshToken();
            if (refreshed) {
                // Retry with new token
                Object.assign(requestHeaders, this.getAuthHeaders());
                response = await fetch(`${this.baseUrl}${endpoint}`, {
                    ...restOptions,
                    method: 'GET',
                    headers: requestHeaders,
                });
            }
        }

        if (!response.ok) {
            throw new ApiError(
                'Failed to fetch blob',
                'BLOB_FETCH_ERROR',
                response.status
            );
        }

        return await response.blob();
    }
}

/**
 * API Error class
 */
export class ApiError extends Error {
    code: string;
    status: number;

    constructor(message: string, code: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
    }
}

// Export singleton instance
export const api = new ApiClient(API_URL);
